import type { Permission } from '@taim/shared';
import {
  BarChart3,
  Briefcase,
  CalendarRange,
  Clock,
  FolderKanban,
  LogOut,
  type LucideIcon,
  Menu,
  Settings,
  Tag,
  Upload,
  Users,
} from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useWarmCatalog } from '@/features/catalog/hooks';
import { useLeaveWarning } from '@/hooks/useLeaveWarning';
import { useAuth, useLogout } from '@/lib/auth';
import { importOrReload } from '@/lib/chunk-reload';
import { cn } from '@/lib/utils';
import { logoDataUrl, TaimLogo } from './TaimLogo';
import { UpdateNotice } from './UpdateNotice';

// Loaded on its own after the page: the header progress never holds up the rest of the app.
const ProgressHeader = lazy(() =>
  importOrReload(() => import('./ProgressHeader')).then((m) => ({ default: m.ProgressHeader })),
);

/** The content area while a page's code loads (pages are separate chunks, see routes.tsx). */
function PageLoading() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground" aria-busy="true">
      Loading…
    </div>
  );
}

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: Permission;
  section?: string;
};

/** Same order and grouping as Clockify's sidebar. */
export const NAV_ITEMS: NavItem[] = [
  { to: '/tracker', label: 'Time Tracker', icon: Clock },
  { to: '/timesheet', label: 'Timesheet', icon: CalendarRange },
  { to: '/reports', label: 'Reports', icon: BarChart3, section: 'Analyze' },
  { to: '/projects', label: 'Projects', icon: FolderKanban, section: 'Manage' },
  { to: '/team', label: 'Team', icon: Users, permission: 'reports:all' },
  { to: '/clients', label: 'Clients', icon: Briefcase },
  { to: '/tags', label: 'Tags', icon: Tag },
  // Time Off (/time-off, icon Plane) is left out of the sidebar for now; its page still works.
  { to: '/settings', label: 'Settings', icon: Settings, permission: 'settings:manage' },
  { to: '/import', label: 'Import', icon: Upload, permission: 'import:run' },
];

const COLLAPSE_KEY = 'taim.sidebarCollapsed';
/** Below this window width the sidebar starts as icons, leaving room for the timesheet's week. */
const WIDE_WINDOW = 1280;

/** The user's choice when they made one; otherwise collapsed on a narrow window. */
function readCollapsed() {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(COLLAPSE_KEY);
  } catch {
    /* storage unavailable */
  }
  return saved === null ? window.innerWidth < WIDE_WINDOW : saved === 'true';
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function AppShell() {
  const { user, tenant, can } = useAuth();
  const logout = useLogout();
  useLeaveWarning();
  // Tags, projects and clients load once, up front: every picker then opens with its list.
  useWarmCatalog();

  // The workspace theme (Settings → Theme) styles the whole page via <html data-theme>.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = tenant.theme;
    // The browser tab's icon follows the theme too.
    const style = getComputedStyle(root);
    const primary = style.getPropertyValue('--primary').trim();
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon && primary) {
      icon.href = logoDataUrl(primary, style.getPropertyValue('--primary-foreground').trim() || undefined);
    }
  }, [tenant.theme]);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, String(next));
    } catch {
      /* storage unavailable */
    }
  };

  const items = NAV_ITEMS.filter((i) => !i.permission || can(i.permission));

  return (
    <div className="flex h-screen flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-topbar px-3">
        <Button
          variant="ghost"
          size="icon"
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          onClick={toggle}
        >
          <Menu className="size-5 text-muted-foreground" />
        </Button>
        <div className="flex items-center gap-2">
          <TaimLogo />
          <span className="text-lg font-medium">Taim</span>
        </div>
        <div className="hidden min-w-0 flex-1 justify-center lg:flex">
          <Suspense fallback={null}>
            <ProgressHeader />
          </Suspense>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-muted-foreground uppercase tracking-wide sm:inline">
            {tenant.name}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" aria-label="Account menu">
                <Avatar className="size-8">
                  {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />}
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {initials(user.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="font-normal">
                <div className="font-medium">{user.name}</div>
                <div className="text-muted-foreground text-xs">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => logout.mutate()}>
                <LogOut className="size-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <UpdateNotice />

      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Main"
          data-collapsed={collapsed}
          className={cn(
            'shrink-0 overflow-y-auto border-r bg-sidebar py-2 transition-[width]',
            collapsed ? 'w-14' : 'w-52',
          )}
        >
          {items.map((item) => (
            <div key={item.to}>
              {item.section && !collapsed && (
                <div className="px-4 pt-4 pb-1 text-[11px] text-muted-foreground/80 uppercase tracking-wider">
                  {item.section}
                </div>
              )}
              <NavLink
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex h-11 items-center gap-3 px-4 text-sidebar-foreground uppercase text-[13px] tracking-wide hover:bg-muted',
                    isActive && 'bg-sidebar-active text-foreground',
                  )
                }
              >
                <item.icon className="size-5 shrink-0" aria-hidden />
                <span className={cn(collapsed && 'sr-only')}>{item.label}</span>
              </NavLink>
            </div>
          ))}
        </nav>
        <main className="min-w-0 flex-1 overflow-y-auto">
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
